import React from 'react';
import { Animated, View, Text, TouchableOpacity } from 'react-native';
import { useNavigationState } from '@react-navigation/native';
import i18n from 'i18n-js';

import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import { RESTRICTED_ROOT_ROUTES } from '@config/tenantRestrictions';
import { role, scale } from '@components/frame';
import { WithColor } from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import Button from '@styles/buttons';
import { getBoxShadowStyle } from '@styles/shadow';
import { modalBg } from '@v2/pages/common/constants/base.constants';

import UserImage from '@components/common/UserImage';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';

import { ACCOUNT_AVATAR_SIZE } from './constants';
import { useManageProfileDropdown, getAccountInitial } from './hooks';

export interface ProfileDropdownProps extends WithColor {
  triggerOverlay: (value?: boolean) => void;
}

const ProfileDropdown = ({ C, triggerOverlay }: ProfileDropdownProps) => {
  const activeRouteName = useNavigationState((state) => state?.routes?.[state.index]?.name);
  const isRestrictedTenant = (RESTRICTED_ROOT_ROUTES as readonly string[]).includes(activeRouteName ?? '');

  const {
    firstName,
    lastName,
    emailAddress,
    allAccounts,
    activeAccountId,
    hasMultipleAccounts,
    accountsExpanded,
    expandAnim,
    switchingAccount,
    toggleAccounts,
    handleSwitchAccount,
    handleAddAccount,
    handleLogOff,
    handleLogOffAll,
    handleAddMachine,
    handleAddDevice,
    handleInviteColleague,
  } = useManageProfileDropdown({ triggerOverlay });

  return (
    <TouchableOpacity
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: C('Overlay/Light/Dim strong'),
        position: 'absolute',
        zIndex: 1000000000,
      }}
      onPress={() => triggerOverlay()}
      activeOpacity={1}
    >
      <View style={{ width: '100%', height: '100%', alignItems: 'center' }}>
        <View style={{ width: '100%', paddingRight: scale(18), flexDirection: 'row', justifyContent: 'flex-end' }}>
          <View
            style={[
              getBoxShadowStyle({ type: 'Narrow', C }),
              {
                backgroundColor: C(modalBg),
                width: scale(328),
                borderRadius: scale(9),
                borderWidth: 1,
                borderColor: C('Hairline/Light/Primary'),
                marginTop: scale(4),
                paddingBottom: scale(20),
                alignItems: 'center',
              },
            ]}
          >
            <View style={{ marginTop: scale(18) }}>
              <UserImage {...{ showAdd: true }} />
            </View>
            <View style={{ marginTop: scale(16), alignItems: 'center' }}>
              <Text style={[font['Headline'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>
                {firstName} {lastName}
              </Text>
              <Text style={[font['Subhead 2'], { color: C('Label/Light/Secondary'), textAlign: 'center', marginTop: scale(9) }]}>{emailAddress}</Text>
            </View>

            {hasMultipleAccounts && (
              <View style={{ marginTop: scale(18), paddingHorizontal: scale(20), width: '100%' }}>
                <Button {...{ variant: 'Dialog M', type: 'Secondary', onPress: toggleAccounts }}>
                  {accountsExpanded ? i18n.t('Hide more accounts') : i18n.t('Show more accounts')}
                </Button>

                <Animated.View
                  style={{
                    maxHeight: expandAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 500] }),
                    opacity: expandAnim,
                    overflow: 'hidden',
                  }}
                >
                  {allAccounts.map((account) => {
                      const isActive = account.homeAccountId === activeAccountId;
                      const isSwitching = switchingAccount === account.homeAccountId;

                      return (
                        <TouchableElement
                          key={account.homeAccountId}
                          onPress={isActive ? undefined : () => handleSwitchAccount(account.homeAccountId)}
                        >
                          {({ boxStyle, pressHoverColor, hovered, pressed }) => (
                            <View
                              style={[
                                {
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  paddingVertical: scale(10),
                                  paddingHorizontal: scale(16),
                                  borderRadius: scale(10),
                                },
                                isActive && { backgroundColor: C('Selected/Light/List item') },
                                !isActive && (hovered || pressed) && { backgroundColor: C(pressHoverColor('Background/Light/Field')) },
                                boxStyle,
                              ]}
                            >
                              {isActive ? (
                                <View style={{ width: scale(ACCOUNT_AVATAR_SIZE), height: scale(ACCOUNT_AVATAR_SIZE), borderRadius: scale(ACCOUNT_AVATAR_SIZE) / 2, overflow: 'hidden' }}>
                                  <UserImage size={ACCOUNT_AVATAR_SIZE} />
                                </View>
                              ) : (
                                <View
                                  style={{
                                    width: scale(ACCOUNT_AVATAR_SIZE),
                                    height: scale(ACCOUNT_AVATAR_SIZE),
                                    borderRadius: scale(ACCOUNT_AVATAR_SIZE) / 2,
                                    backgroundColor: C('Background/Light/Field'),
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Text style={[font['Subhead 2'], { color: C('Label/Light/Secondary') }]}>
                                    {getAccountInitial(account)}
                                  </Text>
                                </View>
                              )}
                              <View style={{ flex: 1, marginLeft: scale(12) }}>
                                <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]} numberOfLines={1}>
                                  {account.name || account.username}
                                </Text>
                                <Text style={[font['Caption 1'], { color: C('Label/Light/Secondary'), marginTop: scale(2) }]} numberOfLines={1}>
                                  {account.username}
                                </Text>
                              </View>
                              <View style={{ width: scale(20), alignItems: 'center' }}>
                                {isSwitching ? (
                                  <SmallLoading />
                                ) : isActive ? (
                                  <Svg icon="check" color="Selected/Light/Primary" width={scale(15)} height={scale(15)} />
                                ) : null}
                              </View>
                            </View>
                          )}
                        </TouchableElement>
                      );
                    })}

                    <TouchableElement onPress={handleAddAccount}>
                      {({ boxStyle, pressHoverColor, hovered, pressed }) => (
                        <View
                          style={[
                            {
                              flexDirection: 'row',
                              alignItems: 'center',
                              paddingVertical: scale(10),
                              paddingHorizontal: scale(16),
                              borderRadius: scale(10),
                            },
                            (hovered || pressed) && { backgroundColor: C(pressHoverColor('Background/Light/Field')) },
                            boxStyle,
                          ]}
                        >
                          <View
                            style={{
                              width: scale(ACCOUNT_AVATAR_SIZE),
                              height: scale(ACCOUNT_AVATAR_SIZE),
                              borderRadius: scale(ACCOUNT_AVATAR_SIZE) / 2,
                              backgroundColor: C('Background/Light/Field'),
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Svg icon="plusSmall" width={scale(14)} height={scale(14)} color="Label/Light/Secondary" />
                          </View>
                          <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary'), marginLeft: scale(12) }]}>
                            {i18n.t('Add another account')}
                          </Text>
                        </View>
                      )}
                    </TouchableElement>

                    <TouchableElement onPress={handleLogOffAll}>
                      {({ boxStyle, pressHoverColor, hovered, pressed }) => (
                        <View
                          style={[
                            {
                              paddingVertical: scale(10),
                              paddingHorizontal: scale(16),
                              borderRadius: scale(10),
                            },
                            (hovered || pressed) && { backgroundColor: C(pressHoverColor('Background/Light/Field')) },
                            boxStyle,
                          ]}
                        >
                          <Text style={[font['Subhead 2'], { color: C('Chart/Light/Warning') }]}>{i18n.t('Log Off All Accounts')}</Text>
                        </View>
                      )}
                    </TouchableElement>
                </Animated.View>
              </View>
            )}

            {!hasMultipleAccounts && (
              <View style={{ marginTop: scale(25), paddingHorizontal: scale(20), width: '100%' }}>
                <Button {...{ variant: 'Dialog M', type: 'Secondary', onPress: handleAddAccount }}>{i18n.t('Add Account')}</Button>
              </View>
            )}
            {!isRestrictedTenant && !isRestrictedForOperator(role, RESTRICTION_CATEGORIES.PROFILE_ACTIONS, 'Add Machine') && (
              <View style={{ marginTop: scale(18), paddingHorizontal: scale(20), width: '100%' }}>
                <Button {...{ variant: 'Dialog M', type: 'Secondary', onPress: handleAddMachine }}>{i18n.t('Add Machine')}</Button>
              </View>
            )}
            {!isRestrictedTenant && (
              <View style={{ marginTop: scale(18), paddingHorizontal: scale(20), width: '100%' }}>
                <Button {...{ variant: 'Dialog M', type: 'Secondary', onPress: handleAddDevice }}>{i18n.t('Add Device')}</Button>
              </View>
            )}
            {!isRestrictedTenant && !isRestrictedForOperator(role, RESTRICTION_CATEGORIES.PROFILE_ACTIONS, 'Invite Colleague') && (
              <View style={{ marginTop: scale(18), paddingHorizontal: scale(20), width: '100%' }}>
                <Button {...{ variant: 'Dialog M', type: 'Secondary', onPress: handleInviteColleague }}>{i18n.t('Invite Colleague')}</Button>
              </View>
            )}
            <View style={{ marginTop: scale(18), paddingHorizontal: scale(20), width: '100%' }}>
              <Button {...{ variant: 'Dialog M', type: 'Primary', onPress: handleLogOff }}>{i18n.t('Log out')}</Button>
            </View>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default ProfileDropdown;
